// src/utils/tactic/boardExport.js
// ─────────────────────────────────────────────────────────────────────────
// Flatten the board — map render plus the SVG overlay — into one PNG.
//
// WHY NOT html2canvas
// The board is an <img> with a live SVG on top of it, and the overlay leans on
// a mask, a blur filter and <image> nodes. html2canvas reimplements CSS layout
// and gets those wrong. Rasterising the SVG through the browser's own renderer
// is both simpler and exact.
//
// THE ONE CATCH
// An SVG loaded into an <img> is a closed box: it may not fetch anything
// external, so every hero portrait has to be inlined as a data URI first, and
// webfonts do not apply (text falls back to a system face).
// ─────────────────────────────────────────────────────────────────────────

const XLINK = "http://www.w3.org/1999/xlink";

/** Read a same-origin asset as a data: URI. */
async function toDataUri(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${res.status} ${url}`);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("โหลดรูปไม่สำเร็จ"));
    img.src = src;
  });
}

/**
 * Clone the overlay with every external reference inlined.
 * Portraits that fail to load are dropped rather than left as a broken box.
 */
async function inlineSvg(svg, width, height) {
  const clone = svg.cloneNode(true);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("xmlns:xlink", XLINK);
  clone.setAttribute("width", width);
  clone.setAttribute("height", height);
  // The live node is absolutely positioned by CSS that will not travel with it.
  clone.removeAttribute("style");

  const cache = new Map();
  for (const node of [...clone.querySelectorAll("image")]) {
    const href = node.getAttribute("href") || node.getAttributeNS(XLINK, "href");
    if (!href || href.startsWith("data:")) continue;

    if (!cache.has(href)) {
      cache.set(
        href,
        await toDataUri(href).catch(() => null)
      );
    }
    const inlined = cache.get(href);
    if (inlined) {
      node.setAttribute("href", inlined);
      node.setAttributeNS(XLINK, "xlink:href", inlined);
    } else {
      node.remove();
    }
  }

  return new XMLSerializer().serializeToString(clone);
}

/**
 * Compose the board into a PNG blob.
 *
 * @param {SVGSVGElement} svg      the live overlay
 * @param {string} mapSrc          the map render behind it
 * @param {number} width,height    output pixels (the SVG's own viewBox size)
 */
export async function renderBoardPng({ svg, mapSrc, width, height }) {
  if (!svg) throw new Error("ยังไม่พร้อมส่งออก");

  const markup = await inlineSvg(svg, width, height);
  const svgUrl = URL.createObjectURL(new Blob([markup], { type: "image/svg+xml" }));

  try {
    const [map, overlay] = await Promise.all([
      loadImage(mapSrc).catch(() => null),
      loadImage(svgUrl),
    ]);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "#05070c";
    ctx.fillRect(0, 0, width, height);
    if (map) ctx.drawImage(map, 0, 0, width, height);
    ctx.drawImage(overlay, 0, 0, width, height);

    return await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("สร้างไฟล์รูปไม่สำเร็จ"))),
        "image/png"
      );
    });
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

export function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** True when this browser can put an image on the clipboard. */
export function canCopyImage() {
  return typeof ClipboardItem === "function" && !!navigator.clipboard?.write;
}

export async function copyBlobToClipboard(blob) {
  await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
}
